import type { AgentApiConfig } from './config';

export type MockTransport = { baseUrl: string; fetchImpl: typeof fetch };

/**
 * The scripted agent service, for development builds only. The mock is loaded
 * with a dynamic import() inside a branch on the literal import.meta.env.DEV,
 * which Vite replaces with `false` in production builds. The bundler then
 * drops the branch and the mock never reaches a deployed bundle. Never import
 * ./mock/* statically from anything the panel ships.
 *
 * The mock keeps its runs in memory, so every call in a page load shares one
 * cached transport: a second hook instance must not reset run ids and sessions.
 */
let cachedMock: Promise<MockTransport> | null = null;

export async function loadMockTransport(
  mode: AgentApiConfig['mode'],
): Promise<MockTransport | null> {
  if (import.meta.env.DEV) {
    if (mode !== 'mock') return null;
    cachedMock ??= import('./mock/mockFetch').then(
      ({ createMockFetch, MOCK_BASE_URL }) => ({
        baseUrl: MOCK_BASE_URL,
        // Cut each run's first connection after 3 events. In the sample answer
        // the third is a tool step's `started` and the fourth its `done`, so the
        // resume lands between them and is visible by hand.
        fetchImpl: createMockFetch({ delayMs: 120, dropAfter: 3 }),
      }),
    );
    return cachedMock;
  }
  return null;
}
